/**
 * One budget verdict, wherever it appears: a tile, a table cell, a heading.
 *
 * A CHIP AND A NUMBER, never a colour alone. The five statuses reduce to three
 * tones and two of the five share one, so tone cannot carry the answer by
 * itself - and a reader who cannot distinguish the warning amber from the error
 * red, which is a large minority of readers, would be left with a coloured
 * rectangle and no verdict at all. The words are the answer; the tone agrees
 * with them.
 *
 * The NOTE is a tooltip rather than a footnote for one reason: `not_comparable`
 * is a single status covering three different refusals - a unit that disagrees,
 * a budget on an aggregation that is not a sum, and nothing stated to compare -
 * and the badge is identical for all three. Without the note the screen says
 * "not compared" and gives no way to find out why, which is the same as not
 * reporting it.
 */

import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import type { BudgetReading } from './budget-model'

export const NO_MARGIN_WHY =
  'A margin exists only where the comparison is settled. This total is a floor — ' +
  'something below it states no value — so it can prove an overrun and can never ' +
  'prove headroom. The parts that state nothing could take it over the budget, and ' +
  'subtracting one from the other would give a number that is wrong in the ' +
  'direction somebody designs against.'

export default function BudgetVerdict({
  reading,
  /** Show the budget and margin beside the badge, not only the verdict. */
  detailed = true,
}: {
  reading: BudgetReading
  detailed?: boolean
}) {
  const badge = (
    <Chip
      label={reading.label}
      size="small"
      color={reading.tone}
      variant={reading.tone === 'info' ? 'outlined' : 'filled'}
    />
  )

  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5 }}
    >
      {/* The note explains the verdict, so it belongs on the verdict. */}
      {reading.note ? (
        <Tooltip title={reading.note} describeChild>
          {badge}
        </Tooltip>
      ) : (
        badge
      )}

      {detailed && (
        <>
          <Typography variant="body2" color="text.secondary">
            of {reading.budget}
          </Typography>
          {reading.hasMargin ? (
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {reading.margin}
            </Typography>
          ) : (
            // NOT a dash and not a zero. A dash reads as "not shown here" and
            // invites the reader to do the subtraction themselves, which is
            // exactly the arithmetic that has no answer.
            <Tooltip title={NO_MARGIN_WHY} describeChild>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontStyle: 'italic', textDecoration: 'underline dotted' }}
              >
                no margin
              </Typography>
            </Tooltip>
          )}
        </>
      )}
    </Stack>
  )
}
